import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Clock, KeyRound, Loader2, LogOut, Mail, Phone, RefreshCw, Tag, Trash2 } from "lucide-react";
import { LogoFull } from "../components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminInquiries() {
  const [key, setKey] = useState(sessionStorage.getItem("brah_admin_key") || "");
  const [pin, setPin] = useState(sessionStorage.getItem("brah_admin_pin") || "");
  const [input, setInput] = useState("");
  const [pinInput, setPinInput] = useState("");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchStats = async (k, p) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/visits/stats`, {
        headers: { "X-Admin-Key": k, "X-Admin-Pin": p },
      });
      setStats(res.data);
    } catch (e) {
      // silent — stats are secondary
    }
  };

  const fetchInquiries = async (k, p) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${BACKEND_URL}/api/inquiries?limit=200`, {
        headers: { "X-Admin-Key": k, "X-Admin-Pin": p },
      });
      setItems(res.data);
      setKey(k);
      setPin(p);
      sessionStorage.setItem("brah_admin_key", k);
      sessionStorage.setItem("brah_admin_pin", p);
      fetchStats(k, p);
    } catch (e) {
      setError("الرمز أو PIN غير صحيح — تأكد وحاول مرة أخرى");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const login = (e) => {
    e.preventDefault();
    fetchInquiries(input.trim(), pinInput.trim());
  };

  const removeInquiry = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/inquiries/${id}`, {
        headers: { "X-Admin-Key": key, "X-Admin-Pin": pin },
      });
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch (e) {
      setError("تعذّر حذف الطلب — حاول مرة أخرى");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("brah_admin_key");
    sessionStorage.removeItem("brah_admin_pin");
    setKey("");
    setPin("");
    setItems([]);
    setInput("");
    setPinInput("");
  };

  if (!key) {
    return (
      <div dir="rtl" className="min-h-screen bg-[#060B19] flex items-center justify-center px-5 font-body" data-testid="admin-login-screen">
        <form onSubmit={login} className="glass-card rounded-3xl p-8 sm:p-10 w-full max-w-md" data-testid="admin-login-form">
          <div className="flex justify-center mb-8"><LogoFull /></div>
          <h1 className="font-display font-extrabold text-2xl text-[#F8FAFC] text-center">لوحة طلبات التواصل</h1>
          <p className="mt-2 text-sm text-[#94A3B8] text-center">منطقة خاصة — أدخل الرمز السري للمتابعة</p>
          <div className="mt-7 space-y-4">
            <input
              type="password"
              required
              data-testid="admin-key-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="الرمز السري"
              className="w-full rounded-2xl bg-[#0F1A36]/80 border border-[#D4AF37]/15 px-5 py-3.5 text-[#F8FAFC] placeholder:text-[#64748B] focus:outline-none focus:border-[#D4AF37]/60 transition-colors"
            />
            <input
              type="password"
              inputMode="numeric"
              required
              data-testid="admin-pin-input"
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value)}
              placeholder="PIN المشرف"
              className="w-full rounded-2xl bg-[#0F1A36]/80 border border-[#D4AF37]/15 px-5 py-3.5 text-[#F8FAFC] placeholder:text-[#64748B] focus:outline-none focus:border-[#D4AF37]/60 transition-colors text-center tracking-[0.5em]"
            />
          </div>
          {error && <p className="mt-3 text-sm text-red-400" data-testid="admin-error-message">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-8 py-3.5 font-display font-bold text-[#060B19] hover:bg-[#E5C158] disabled:opacity-60 transition-all"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <KeyRound size={18} />}
            دخول
          </button>
          <a href="/" data-testid="admin-back-home-link" className="mt-5 block text-center text-sm text-[#94A3B8] hover:text-[#E5C158] transition-colors">
            العودة للموقع الرئيسي
          </a>
        </form>
      </div>
    );
  }

  return (
    <div dir="rtl" className="min-h-screen bg-[#060B19] font-body" data-testid="admin-panel">
      <header className="border-b border-[#D4AF37]/15 bg-[#0B132B]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <LogoFull />
          <div className="flex items-center gap-3">
            <button
              onClick={() => fetchInquiries(key, pin)}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-4 py-2 text-sm text-[#E5C158] hover:bg-[#D4AF37]/10 transition-all"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
              تحديث
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-red-400/40 px-4 py-2 text-sm text-red-300 hover:bg-red-400/10 transition-all"
            >
              <LogOut size={15} />
              خروج
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-testid="admin-stats-grid">
            <div className="glass-card rounded-2xl p-5 text-center" data-testid="admin-stat-total">
              <div className="font-display font-black text-3xl gold-text">{stats.total_visits}</div>
              <div className="mt-1 text-sm text-[#94A3B8]">إجمالي الزيارات</div>
            </div>
            <div className="glass-card rounded-2xl p-5 text-center" data-testid="admin-stat-unique">
              <div className="font-display font-black text-3xl gold-text">{stats.unique_visitors}</div>
              <div className="mt-1 text-sm text-[#94A3B8]">زوار فريدون (حسب IP)</div>
            </div>
            <div className="glass-card rounded-2xl p-5 text-center" data-testid="admin-stat-today">
              <div className="font-display font-black text-3xl gold-text">{stats.visits_today}</div>
              <div className="mt-1 text-sm text-[#94A3B8]">زيارات اليوم</div>
            </div>
          </div>
        )}

        <div className="flex items-end justify-between mb-8">
          <div>
            <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-[#F8FAFC]" data-testid="admin-title">طلبات التواصل الواردة</h1>
            <p className="mt-2 text-sm text-[#94A3B8]">كل الطلبات المرسلة من نموذج الموقع</p>
          </div>
          <span className="num-badge rounded-full px-4 py-1.5 text-sm text-[#E5C158]" data-testid="admin-count-badge">
            {items.length} طلب
          </span>
        </div>

        {items.length === 0 && !loading && (
          <div className="glass-card rounded-3xl p-12 text-center text-[#94A3B8]" data-testid="admin-empty-state">
            لا توجد طلبات بعد — ستظهر هنا فور وصولها.
          </div>
        )}

        <div className="space-y-4">
          {items.map((it, i) => (
            <motion.div
              key={it.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: Math.min(i * 0.05, 0.5) }}
              className="glass-card rounded-2xl p-6"
              data-testid={`admin-inquiry-card-${i}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="font-display font-bold text-lg text-[#F8FAFC]" data-testid={`admin-inquiry-name-${i}`}>{it.name}</div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-xs text-[#64748B]" dir="ltr">
                    <Clock size={13} />
                    {new Date(it.timestamp).toLocaleString("ar-SA")}
                  </div>
                  <button
                    onClick={() => removeInquiry(it.id)}
                    data-testid={`admin-delete-button-${i}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-red-400/40 px-3 py-1 text-xs text-red-300 hover:bg-red-400/10 transition-all"
                  >
                    <Trash2 size={13} />
                    حذف
                  </button>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/25 px-3 py-1 text-xs text-[#E5C158]" dir="ltr">
                  <Phone size={12} /> {it.phone}
                </span>
                {it.email && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#38BDF8]/10 border border-[#38BDF8]/25 px-3 py-1 text-xs text-[#7DD3FC]" dir="ltr">
                    <Mail size={12} /> {it.email}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#10B981]/10 border border-[#10B981]/25 px-3 py-1 text-xs text-[#6EE7B7]">
                  <Tag size={12} /> {it.service}
                </span>
              </div>
              <p className="mt-4 font-body text-sm sm:text-base leading-relaxed text-[#CBD5E1] bg-[#0F1A36]/50 rounded-xl px-4 py-3" data-testid={`admin-inquiry-message-${i}`}>
                {it.message}
              </p>
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
}
